import { useMemo, useCallback } from 'react';

import { useQueryParams, StringParam, NumberParam } from 'routing/QueryParams';
import type { TimeRange } from 'views/logic/queries/Query';

type UrlQueryParams = {
  rangetype: string | undefined;
  relative: number | undefined;
  from: string | undefined;
  to: string | undefined;
  keyword: string | undefined;
};

const parseNestedObject = (fieldQueryString: string) => {
  try {
    return JSON.parse(decodeURIComponent(fieldQueryString));
  } catch (_error) {
    return null;
  }
};

const NestedObjectParam = {
  encode: (object: Object | null | undefined) => (object ? encodeURIComponent(JSON.stringify(object)) : undefined),
  decode: (objectStr: string | null | undefined) => parseNestedObject(objectStr),
};

const QUERY_PARAM_CONFIG_MAP = {
  rangetype: StringParam,
  from: StringParam,
  to: StringParam,
  range: NumberParam,
  streams: StringParam,
  keyword: StringParam,
  relative: NumberParam,
  q: StringParam,
  fields: NestedObjectParam,
  dataLakeId: StringParam,
};

const initialTimeRange = (queryParams: UrlQueryParams): TimeRange => {
  switch (queryParams.rangetype) {
    case 'relative':
      if (queryParams.relative !== undefined) {
        return {
          type: queryParams.rangetype,
          range: queryParams.relative,
        };
      }

      return {
        type: queryParams.rangetype,
        from: queryParams.from ? Number(queryParams.from) : undefined,
        to: queryParams.to ? Number(queryParams.to) : undefined,
      };
    case 'absolute':
      return {
        type: queryParams.rangetype,
        from: queryParams.from,
        to: queryParams.to,
      };
    case 'keyword':
      return {
        type: queryParams.rangetype,
        keyword: queryParams.keyword,
      };
    default:
      return undefined;
  }
};

const useUrlParams = () => {
  const [query, setQuery] = useQueryParams(QUERY_PARAM_CONFIG_MAP);

  const queryStream = query.streams?.split(',')[0];
  const urlParams = useMemo(
    () => ({
      timerange: initialTimeRange(query),
      stream: queryStream,
      fields: query.fields,
      dataLakeId: query.dataLakeId,
    }),
    [query, queryStream],
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const initialUrlParams = useMemo(() => urlParams, []);

  const resetUrlQueryParams = useCallback(() => {
    setQuery({
      from: undefined,
      to: undefined,
      keyword: undefined,
      rangetype: undefined,
      range: undefined,
      streams: undefined,
      fields: undefined,
      dataLakeId: undefined,
    });
  }, [setQuery]);

  return { initialUrlParams, urlParams, resetUrlQueryParams };
};

export default useUrlParams;
